// components/invoices/InvoiceList.tsx - FIXED: Restore edit functionality (PART 1)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Sync as SyncIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  DeleteForever as DeleteForeverIcon,
 CheckCircle as CheckCircleIcon

} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from '../../utils/axiosConfig';

type InvoiceStatus = 'draft' | 'submitted' | 'approved' | 'paid' | 'rejected' | 'on bank';
type ArchiveReason = 'completed' | 'cancelled' | 'expired' | 'duplicate' | 'other';

interface Invoice {
  _id: string;
  invoiceNumber: string;
  staffId: {
    _id: string;
    name: string;
    email: string;
  };
  invoiceDate: string;
  dueDate: string;
  status: InvoiceStatus;
  totalAmount: number;
  lineItems: Array<{
    itemName: string;
    totalAmount: number;
    mondayEventId: string;
    imcCode: string;
    activityName?: string;
    imcCodeDescription?: string;
  }>;
  createdAt: string;
  submittedAt?: string;
  pdfUrl?: string;
  mondayItemId?: string;
  mondayUrl?: string;
  mondayIntegration?: {
    items: Array<{
      itemId: string;
      itemName: string;
    }>;
  };
  mondayStatusSync?: {
    lastChecked: Date;
    mondayStatus: string;
  };
  // Archive fields
  isArchived?: boolean;
  archiveDate?: string;
  archiveReason?: ArchiveReason;
  archiveNotes?: string;
  archivedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  // Enhanced display info from backend
  displayInfo?: {
    primaryActivity: string;
    primaryImcCode: string;
    itemSummary: string;
  };
}

interface User {
  role: string;
  email: string;
}

const STATUS_COLORS: Record<InvoiceStatus, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  draft: 'default',
  submitted: 'warning',
  approved: 'info',
  paid: 'success',
  rejected: 'error',
  'on bank': 'info'
};

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  paid: 'Paid',
  rejected: 'Rejected',
  'on bank': 'On Bank'
};

const ARCHIVE_REASON_LABELS: Record<ArchiveReason, string> = {
  completed: 'Completed',
  cancelled: 'Cancelled',
  expired: 'Expired',
  duplicate: 'Duplicate',
  other: 'Other'
};

const InvoiceList: React.FC = () => {
  const navigate = useNavigate();
  
  // State
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [globalSyncing, setGlobalSyncing] = useState(false);
  
  // Tab state for archive filter
  const [tabValue, setTabValue] = useState(0); // 0: Active, 1: Archived
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [archiveReasonFilter, setArchiveReasonFilter] = useState<string>('all');
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  
  // Archive dialog state
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState<ArchiveReason>('completed');
  const [archiveNotes, setArchiveNotes] = useState('');
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchInvoices();
    fetchCurrentUser();
  }, [statusFilter, tabValue, archiveReasonFilter]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axiosInstance.get('/api/users/me');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {};
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      // Filter by archive status based on tab
      if (tabValue === 0) {
        params.archived = 'false'; // Active invoices only
      } else {
        params.archived = 'true'; // Archived invoices only
        
        if (archiveReasonFilter !== 'all') {
          params.archiveReason = archiveReasonFilter;
        }
      }
      
      const response = await axiosInstance.get('/api/invoices', { params });
      setInvoices(response.data.invoices);
      
    } catch (err: any) {
      console.error('Error fetching invoices:', err);
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };
  // RESTORED: Original helper functions for displaying activity and IMC code
 const getPrimaryActivity = (invoice: Invoice): string => {
   // First try enhanced display info from backend
   if (invoice.displayInfo?.primaryActivity) {
     return invoice.displayInfo.primaryActivity;
   }
   
   if (!invoice.lineItems || invoice.lineItems.length === 0) return 'No Activity';
   
   // Use stored activityName if available
   const activitiesWithNames = invoice.lineItems
     .map(item => item.activityName)
     .filter((activity): activity is string => Boolean(activity));
   
   if (activitiesWithNames.length > 0) {
     const activityCounts = activitiesWithNames.reduce((acc, activity) => {
       acc[activity] = (acc[activity] || 0) + 1;
       return acc;
     }, {} as Record<string, number>);
     
     const mostCommon = Object.keys(activityCounts).reduce((a, b) => 
       activityCounts[a] > activityCounts[b] ? a : b
     );
     
     return mostCommon.length > 25 ? mostCommon.substring(0, 25) + '...' : mostCommon;
   }
   
   // Fallback to mondayEventId (shortened)
   const activities = invoice.lineItems
     .map(item => item.mondayEventId)
     .filter((activity): activity is string => Boolean(activity));
     
   if (activities.length === 0) return 'No Activity';
   
   const activityCounts = activities.reduce((acc, activity) => {
     acc[activity] = (acc[activity] || 0) + 1;
     return acc;
   }, {} as Record<string, number>);
   
   const mostCommon = Object.keys(activityCounts).reduce((a, b) => 
     activityCounts[a] > activityCounts[b] ? a : b
   );
   
   return mostCommon ? mostCommon.substring(0, 15) + '...' : 'Multiple Activities';
 };

 const getPrimaryIMCCode = (invoice: Invoice): string => {
   // First try enhanced display info from backend
   if (invoice.displayInfo?.primaryImcCode) {
     return invoice.displayInfo.primaryImcCode;
   }
   
   if (!invoice.lineItems || invoice.lineItems.length === 0) return 'No Code';
   
   // Use stored imcCodeDescription if available
   const codesWithDescriptions = invoice.lineItems
     .map(item => item.imcCodeDescription)
     .filter((code): code is string => Boolean(code));
   
   if (codesWithDescriptions.length > 0) {
     const codeCounts = codesWithDescriptions.reduce((acc, code) => {
       acc[code] = (acc[code] || 0) + 1;
       return acc;
     }, {} as Record<string, number>);
     
     const mostCommon = Object.keys(codeCounts).reduce((a, b) => 
       codeCounts[a] > codeCounts[b] ? a : b
     );
     
     return mostCommon.length > 20 ? mostCommon.substring(0, 20) + '...' : mostCommon;
   }
   
   // Fallback: Use raw IMC code
   const imcCodes = invoice.lineItems
     .map(item => item.imcCode)
     .filter((code): code is string => Boolean(code));
     
   if (imcCodes.length === 0) return 'No Code';
   
   const codeCounts = imcCodes.reduce((acc, code) => {
     acc[code] = (acc[code] || 0) + 1;
     return acc;
   }, {} as Record<string, number>);
   
   const mostCommonCode = Object.keys(codeCounts).reduce((a, b) => 
     codeCounts[a] > codeCounts[b] ? a : b
   );
   
   return mostCommonCode;
 };
 
 const handleSyncStatus = async (invoiceId: string) => {
   try {
     setSyncing(invoiceId);
     const response = await axiosInstance.post(`/api/invoices/${invoiceId}/sync-status`);
     
     setInvoices(prev => prev.map(inv => 
       inv._id === invoiceId 
         ? { ...inv, status: response.data.invoice?.status || inv.status }
         : inv
     ));
     
     alert(response.data.message);
   } catch (error: any) {
     console.error('Error syncing status:', error);
     alert(error.response?.data?.message || 'Failed to sync status');
   } finally {
     setSyncing(null);
   }
 };

 const handleSyncAllStatuses = async () => {
   try {
     setGlobalSyncing(true);
     const response = await axiosInstance.post('/api/invoices/sync-all');
     
     alert(`Sync complete! ${response.data.statusChanges} out of ${response.data.totalChecked} invoices had status changes.`);
     fetchInvoices(); // Refresh the list
   } catch (error: any) {
     console.error('Error syncing all statuses:', error);
     alert(error.response?.data?.message || 'Failed to sync statuses');
   } finally {
     setGlobalSyncing(false);
   }
 };

 const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
   setAnchorEl(event.currentTarget);
   setSelectedInvoice(invoice);
 };

 const handleMenuClose = () => {
   setAnchorEl(null);
   setSelectedInvoice(null);
 };

 // ✅ FIXED: Enhanced edit function with better error handling
 const handleEdit = () => {
   if (selectedInvoice) {
     console.log('🔧 Edit invoice:', selectedInvoice.invoiceNumber, 'Status:', selectedInvoice.status);
     navigate(`/admin/invoices/edit/${selectedInvoice._id}`);
   }
   handleMenuClose();
 };

 const handleView = () => {
   if (selectedInvoice) {
     navigate(`/admin/invoices/${selectedInvoice._id}`);
   }
   handleMenuClose();
 };

 const handleDownload = async () => {
   if (!selectedInvoice || !selectedInvoice.pdfUrl) {
     alert('PDF not available for this invoice');
     return;
   }
   
   try {
     const response = await axiosInstance.get(`/api/invoices/${selectedInvoice._id}/download`, {
       responseType: 'blob'
     });
     
     const url = window.URL.createObjectURL(new Blob([response.data]));
     const link = document.createElement('a');
     link.href = url;
     link.setAttribute('download', `${selectedInvoice.invoiceNumber}_invoice.pdf`);
     document.body.appendChild(link);
     link.click();
     link.remove();
     window.URL.revokeObjectURL(url);
     
   } catch (error) {
     console.error('Error downloading invoice:', error);
     alert('Failed to download invoice');
   }
   
   handleMenuClose();
 };

 // Archive handlers
 const handleArchiveClick = () => {
   setArchiveDialogOpen(true);
   setAnchorEl(null); // Close the menu but keep selectedInvoice
 };

 const handleArchiveDialogClose = () => {
   setArchiveDialogOpen(false);
   setSelectedInvoice(null); // Reset here instead
   setArchiveReason('completed');
   setArchiveNotes('');
 };

 const handleArchiveConfirm = async () => {
   if (!selectedInvoice) return;
   
   try {
     await axiosInstance.post(`/api/invoices/${selectedInvoice._id}/archive`, {
       reason: archiveReason,
       notes: archiveNotes
     });
     
     setInvoices(prev => prev.filter(inv => inv._id !== selectedInvoice._id));
     
     alert('Invoice archived successfully');
     setArchiveDialogOpen(false);
     setArchiveReason('completed');
     setArchiveNotes('');
     
   } catch (error: any) {
     console.error('Error archiving invoice:', error);
     alert(error.response?.data?.message || 'Failed to archive invoice');
   }
 };

 const handleUnarchive = async (invoice: Invoice) => {
   try {
     await axiosInstance.post(`/api/invoices/${invoice._id}/unarchive`);
     
     setInvoices(prev => prev.filter(inv => inv._id !== invoice._id));
     
     alert('Invoice unarchived successfully');
   } catch (error: any) {
     console.error('Error unarchiving invoice:', error);
     alert(error.response?.data?.message || 'Failed to unarchive invoice');
   }
 };

const handleDeleteClick = () => {
  setDeleteDialogOpen(true); // do NOT clear selectedInvoice yet
};

const handleDeleteConfirm = async () => {
  if (!selectedInvoice) return;

  try {
    await axiosInstance.delete(`/api/invoices/${selectedInvoice._id}`);
    
    setInvoices(prev => prev.filter(inv => inv._id !== selectedInvoice._id));
    alert('Invoice deleted successfully');
    setDeleteDialogOpen(false);
    handleMenuClose(); // ✅ now it's safe to clear selectedInvoice
  } catch (error: any) {
    console.error('Error deleting invoice:', error);
    alert(error.response?.data?.message || 'Failed to delete invoice');
  }
};

 const filteredInvoices = invoices.filter(invoice => {
   if (searchTerm) {
     const searchLower = searchTerm.toLowerCase();
     return (
       invoice.invoiceNumber.toLowerCase().includes(searchLower) ||
       invoice.staffId.name.toLowerCase().includes(searchLower) ||
       invoice.lineItems.some(item => 
         item.itemName.toLowerCase().includes(searchLower)
       )
     );
   }
   return true;
 });

 // ✅ FIXED: Enhanced permission functions to include 'team' role
const canEditInvoice = (invoice: Invoice): boolean => {
  if (currentUser?.role === 'admin' || currentUser?.role === 'gm' || currentUser?.role === 'team') {
    return !invoice.isArchived; // Admins can edit any non-archived invoice
  }
  return ['draft', 'rejected'].includes(invoice.status) && !invoice.isArchived && 
         invoice.staffId.email === currentUser?.email;
};

const canDeleteInvoice = (invoice: Invoice): boolean => {
  return !invoice.isArchived && (
    currentUser?.role === 'admin' ||
    currentUser?.role === 'gm' ||
    currentUser?.role === 'team' ||
    invoice.staffId.email === currentUser?.email
  );
};


const canArchiveInvoice = (invoice: Invoice): boolean => {
  return !invoice.isArchived && (
    currentUser?.role === 'admin' ||
    currentUser?.role === 'gm' ||
    currentUser?.role === 'team' ||
    invoice.staffId.email === currentUser?.email
  );
};

const canSyncInvoice = (invoice: Invoice): boolean => {
  return !!(invoice.mondayIntegration?.items?.[0]?.itemId);
};

 if (loading) {
   return (
     <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
       <CircularProgress />
       <Typography sx={{ ml: 2 }}>Loading invoices...</Typography>
     </Box>
   );
 }

 return (
   <Container maxWidth="lg">
     <Box sx={{ py: 4 }}>
       
       {/* Header */}
       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
         <Typography variant="h4" component="h1">
           Invoices
         </Typography>
         <Box sx={{ display: 'flex', gap: 2 }}>
           <Button
             variant="outlined"
             startIcon={globalSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
             onClick={handleSyncAllStatuses}
             disabled={globalSyncing}
           >
             {globalSyncing ? 'Syncing...' : 'Sync All with Monday'}
           </Button>
           <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/invoices/new')}>
             Create Invoice
           </Button>
         </Box>
       </Box>

       {/* Archive Tabs */}
       <Tabs 
         value={tabValue} 
         onChange={(_, newValue) => setTabValue(newValue)}
         sx={{ mb: 3 }}
       >
         <Tab label="Active Invoices" />
         <Tab label="Archived Invoices" />
       </Tabs>

       {/* Filters */}
       <Card sx={{ mb: 3 }}>
         <CardContent>
           <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
             <TextField
               label="Search invoices..."
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               size="small"
               sx={{ minWidth: 200 }}
             />
             
             <FormControl size="small" sx={{ minWidth: 150 }}>
               <InputLabel>Status</InputLabel>
               <Select
                 value={statusFilter}
                 onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
                 label="Status"
               >
                 <MenuItem value="all">All Statuses</MenuItem>
                 <MenuItem value="draft">Draft</MenuItem>
                 <MenuItem value="submitted">Submitted</MenuItem>
                 <MenuItem value="approved">Approved</MenuItem>
                 <MenuItem value="paid">Paid</MenuItem>
                 <MenuItem value="on bank">On Bank</MenuItem>
                 <MenuItem value="rejected">Rejected</MenuItem>
               </Select>
             </FormControl>

             {/* Archive reason filter (only show for archived tab) */}
             {tabValue === 1 && (
               <FormControl size="small" sx={{ minWidth: 150 }}>
                 <InputLabel>Archive Reason</InputLabel>
                 <Select
                   value={archiveReasonFilter}
                   onChange={(e: SelectChangeEvent) => setArchiveReasonFilter(e.target.value)}
                   label="Archive Reason"
                 >
                   <MenuItem value="all">All Reasons</MenuItem>
                   <MenuItem value="completed">Completed</MenuItem>
                   <MenuItem value="cancelled">Cancelled</MenuItem>
                   <MenuItem value="expired">Expired</MenuItem>
                   <MenuItem value="duplicate">Duplicate</MenuItem>
                   <MenuItem value="other">Other</MenuItem>
                 </Select>
               </FormControl>
             )}
             
             <Button variant="outlined" onClick={fetchInvoices}>
               Refresh
             </Button>
           </Stack>
         </CardContent>
       </Card>

       {/* Error Display */}
       {error && (
         <Alert severity="error" sx={{ mb: 3 }}>
           {error}
         </Alert>
       )}

       {/* Invoice Cards */}
       <Grid container spacing={3}>
         {filteredInvoices.length > 0 ? (
           filteredInvoices.map((invoice) => (
             <Grid item xs={12} sm={6} md={4} key={invoice._id}>
<Card sx={{ 
  height: '100%', 
  display: 'flex', 
  flexDirection: 'column',
  
  // 🎨 ENHANCED: Special styling for paid invoices
  ...(invoice.status === 'paid' && {
    bgcolor: 'success.50', // Light green background
    border: '2px solid',
    borderColor: 'success.main',
    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.15)', // Subtle green glow
    transform: 'translateY(-1px)', // Slight lift effect
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      boxShadow: '0 6px 20px rgba(76, 175, 80, 0.25)',
      transform: 'translateY(-2px)',
    }
  }),
  
  // 🎨 ENHANCED: Special styling for 'on bank' status  
  ...(invoice.status === 'on bank' && {
    bgcolor: 'info.50', // Light blue background
    border: '2px solid',
    borderColor: 'info.main',
    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)',
  }),
  
  // Archive styling (unchanged)
  opacity: invoice.isArchived ? 0.7 : 1,
  border: invoice.isArchived ? '1px dashed' : (
    invoice.status === 'paid' || invoice.status === 'on bank' ? undefined : '1px solid'
  ),
  borderColor: invoice.isArchived ? 'warning.main' : 'divider'
}}>
                 <CardContent sx={{ flexGrow: 1 }}>
                   
                   {/* Header: Invoice Number + Status + Archive Badge */}
                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                     <Box>
                       <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                         {invoice.invoiceNumber}
                       </Typography>
                       {/* Archive badge */}
                       {invoice.isArchived && (
                         <Chip
                           icon={<ArchiveIcon />}
                           label={`Archived: ${ARCHIVE_REASON_LABELS[invoice.archiveReason || 'other']}`}
                           size="small"
                           color="warning"
                           sx={{ mt: 0.5 }}
                         />
                       )}
                     </Box>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
<Chip
  label={STATUS_LABELS[invoice.status as InvoiceStatus]}
  color={STATUS_COLORS[invoice.status as InvoiceStatus]}
  size="small"
  sx={{ 
    fontWeight: 'bold',
    // 🎨 ENHANCED: Special styling for paid status chip
    ...(invoice.status === 'paid' && {
      bgcolor: 'success.main',
      color: 'white',
      fontWeight: 'bold',
      '& .MuiChip-icon': {
        color: 'white'
      }
    }),
    ...(invoice.status === 'on bank' && {
      bgcolor: 'info.main',
      color: 'white',
      fontWeight: 'bold'
    })
  }}
/>
                       <IconButton
                         size="small"
                         onClick={(e) => handleMenuOpen(e, invoice)}
                       >
                         <MoreIcon />
                       </IconButton>
                     </Box>
                   </Box>

                   {/* WHO: Staff Member - Large and prominent */}
                   <Typography variant="h6" sx={{ color: 'primary.main', mb: 2, fontWeight: 'bold' }}>
                     {invoice.staffId.name}
                   </Typography>

                   {/* WHAT: Primary Activity */}
                   <Box sx={{ mb: 1 }}>
                     <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                       ACTIVITY
                     </Typography>
                     <Typography variant="body2" sx={{ fontWeight: 500 }}>
                       {getPrimaryActivity(invoice)}
                     </Typography>
                   </Box>

                   {/* Primary IMC Code */}
                   <Box sx={{ mb: 2 }}>
                     <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                       IMC CODE
                     </Typography>
                     <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                       {getPrimaryIMCCode(invoice)}
                     </Typography>
                   </Box>

                   {/* Amount - Large and prominent */}
<Typography 
  variant="h5" 
  sx={{ 
    fontWeight: 'bold', 
    mb: 2,
    // 🎨 ENHANCED: Extra emphasis for paid amounts
    color: invoice.status === 'paid' ? 'success.dark' : 'success.main',
    ...(invoice.status === 'paid' && {
      fontSize: '1.5rem',
      textShadow: '0 1px 2px rgba(0,0,0,0.1)',
    })
  }}
>
  €{invoice.totalAmount.toFixed(2)}
  {/* 🎨 NEW: Add checkmark for paid invoices */}
  {invoice.status === 'paid' && (
    <CheckCircleIcon 
      sx={{ 
        ml: 1, 
        color: 'success.main',
        fontSize: '1.2rem'
      }} 
    />
  )}
</Typography>

                   {/* RESTORED: Original line items preview with activity and IMC details */}
                   <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                     {invoice.lineItems.length} item{invoice.lineItems.length !== 1 ? 's' : ''}
                     {invoice.lineItems.length > 0 && (
                       <span> - {invoice.lineItems[0].itemName}
                       {invoice.lineItems.length > 1 && ` +${invoice.lineItems.length - 1} more`}
                       </span>
                     )}
                   </Typography>

                   {/* RESTORED: Enhanced line item details */}
                   {invoice.lineItems.length > 0 && (
                     <>
                       <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                         <strong>Activity:</strong> {invoice.lineItems[0].activityName || 'Unknown Activity'}
                       </Typography>
                       <Typography variant="body2" color="text.secondary">
                         <strong>IMC:</strong> {invoice.lineItems[0].imcCodeDescription || invoice.lineItems[0].imcCode}
                       </Typography>
                     </>
                   )}

                   {/* Archive info */}
                   {invoice.isArchived && invoice.archiveDate && (
                     <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, mt: 2 }}>
                       Archived: {format(new Date(invoice.archiveDate), 'dd/MM/yyyy')}
                       {invoice.archivedBy && ` by ${invoice.archivedBy.name}`}
                     </Typography>
                   )}

                   {/* Dates */}
                   <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                     Created: {format(new Date(invoice.createdAt), 'dd/MM/yyyy')}
                   </Typography>
                   <Typography variant="caption" color="text.secondary">
                     Due: {format(new Date(invoice.dueDate), 'dd/MM/yyyy')}
                   </Typography>

                   {/* RESTORED: Monday Status Sync Info */}
                   {invoice.mondayStatusSync && (
                     <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                       Monday Status: {invoice.mondayStatusSync.mondayStatus}
                     </Typography>
                   )}

                 </CardContent>

<CardActions
  sx={{
    // 🎨 ENHANCED: Special footer styling for paid invoices
    ...(invoice.status === 'paid' && {
      bgcolor: 'success.100',
      borderTop: '1px solid',
      borderColor: 'success.200',
    }),
    ...(invoice.status === 'on bank' && {
      bgcolor: 'info.100',
      borderTop: '1px solid', 
      borderColor: 'info.200',
    })
  }}
>                    
                   <Button
                     size="small"
                     startIcon={<ViewIcon />}
                     onClick={() => navigate(`/admin/invoices/${invoice._id}`)}
                   >
                     View
                   </Button>
                   
                   {/* ✅ FIXED: Edit button with enhanced visibility and logging */}
                   {canEditInvoice(invoice) && (
                     <Button
                       size="small"
                       startIcon={<EditIcon />}
                       onClick={() => {
                         console.log('🔧 Edit clicked for:', invoice.invoiceNumber);
                         navigate(`/admin/invoices/edit/${invoice._id}`);
                       }}
                       variant="outlined"
                       color="primary"
                     >
                       Edit
                     </Button>
                   )}

                   {/* Unarchive button (for archived invoices) */}
                   {invoice.isArchived && (currentUser?.role === 'admin' || currentUser?.role === 'gm' || currentUser?.role === 'team') && (
                     <Button
                       size="small"
                       startIcon={<UnarchiveIcon />}
                       onClick={() => handleUnarchive(invoice)}
                       color="warning"
                     >
                       Unarchive
                     </Button>
                   )}

                   {/* RESTORED: Sync Status Button with original conditions */}
                   {canSyncInvoice(invoice) && (
                     <Button
                       size="small"
                       startIcon={syncing === invoice._id ? <CircularProgress size={16} /> : <SyncIcon />}
                       onClick={() => handleSyncStatus(invoice._id)}
                       disabled={syncing === invoice._id}
                     >
                       Sync
                     </Button>
                   )}
                 </CardActions>
               </Card>
             </Grid>
           ))
         ) : (
           <Grid item xs={12}>
             <Box sx={{ textAlign: 'center', py: 4 }}>
               <Typography variant="h6" color="text.secondary">
                 No invoices found
               </Typography>
               <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                 {searchTerm || statusFilter !== 'all' || archiveReasonFilter !== 'all'
                   ? 'Try adjusting your filters' 
                   : tabValue === 0 
                     ? 'Create your first invoice to get started'
                     : 'No archived invoices yet'
                 }
               </Typography>
               {(!searchTerm && statusFilter === 'all' && tabValue === 0) && (
                 <Button
                   variant="contained"
                   startIcon={<AddIcon />}
                   onClick={() => navigate('/admin/invoices/new')}
                 >
                   Create Invoice
                 </Button>
               )}
             </Box>
           </Grid>
         )}
       </Grid>

       {/* Context Menu */}
       <Menu
         anchorEl={anchorEl}
         open={Boolean(anchorEl)}
         onClose={handleMenuClose}
       >
         <MenuItem onClick={handleView}>
           <ViewIcon sx={{ mr: 1 }} fontSize="small" />
           View
         </MenuItem>
         
         {/* ✅ FIXED: Edit menu item with enhanced functionality */}
         {selectedInvoice && canEditInvoice(selectedInvoice) && (
           <MenuItem onClick={handleEdit}>
             <EditIcon sx={{ mr: 1 }} fontSize="small" />
             Edit
           </MenuItem>
         )}

         {/* Archive option */}
         {selectedInvoice && !selectedInvoice.isArchived && canArchiveInvoice(selectedInvoice) && (
           <MenuItem onClick={handleArchiveClick}>
             <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
             Archive
           </MenuItem>
         )}

         {/* Unarchive option */}
         {selectedInvoice && selectedInvoice.isArchived && (currentUser?.role === 'admin' || currentUser?.role === 'gm' || currentUser?.role === 'team') && (
           <MenuItem onClick={() => {
             handleMenuClose();
             handleUnarchive(selectedInvoice);
           }}>
             <UnarchiveIcon sx={{ mr: 1 }} fontSize="small" />
             Unarchive
           </MenuItem>
         )}

         {/* RESTORED: Sync option with original conditions */}
         {selectedInvoice && canSyncInvoice(selectedInvoice) && (
           <MenuItem onClick={() => {
             handleMenuClose();
             handleSyncStatus(selectedInvoice._id);
           }}>
             <SyncIcon sx={{ mr: 1 }} fontSize="small" />
             Sync Status with Monday
           </MenuItem>
         )}
         
         {selectedInvoice?.pdfUrl && (
           <MenuItem onClick={handleDownload}>
             <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
             Download PDF
           </MenuItem>
         )}
         
         {/* Permanent Delete option (only for archived invoices) */}
         {selectedInvoice && selectedInvoice.isArchived && (currentUser?.role === 'admin' || currentUser?.role === 'gm' || currentUser?.role === 'team') && (
           <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
             <DeleteForeverIcon sx={{ mr: 1 }} fontSize="small" />
             Permanently Delete
           </MenuItem>
         )}

         {/* RESTORED: Original delete for drafts */}
         {selectedInvoice && canDeleteInvoice(selectedInvoice) && (
           <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
             <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
             Delete
           </MenuItem>
         )}
       </Menu>

       {/* Archive Dialog */}
       <Dialog open={archiveDialogOpen} onClose={handleArchiveDialogClose}>
         <DialogTitle>Archive Invoice</DialogTitle>
         <DialogContent>
           <Typography variant="body2" sx={{ mb: 2 }}>
             Archive invoice {selectedInvoice?.invoiceNumber}? This will move it out of the active list.
           </Typography>
           
           <FormControl fullWidth sx={{ mb: 2 }}>
             <InputLabel>Archive Reason</InputLabel>
             <Select
               value={archiveReason}
               onChange={(e) => setArchiveReason(e.target.value as ArchiveReason)}
               label="Archive Reason"
             >
               <MenuItem value="completed">Completed</MenuItem>
               <MenuItem value="cancelled">Cancelled</MenuItem>
               <MenuItem value="expired">Expired</MenuItem>
               <MenuItem value="duplicate">Duplicate</MenuItem>
               <MenuItem value="other">Other</MenuItem>
             </Select>
           </FormControl>

           <TextField
             fullWidth
             label="Notes (optional)"
             multiline
             rows={3}
             value={archiveNotes}
             onChange={(e) => setArchiveNotes(e.target.value)}
             placeholder="Add any additional notes about why this invoice is being archived..."
           />
         </DialogContent>
         <DialogActions>
           <Button onClick={handleArchiveDialogClose}>Cancel</Button>
           <Button onClick={handleArchiveConfirm} variant="contained" color="warning">
             Archive Invoice
           </Button>
         </DialogActions>
       </Dialog>

       {/* Delete Confirmation Dialog */}
       <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
         <DialogTitle>
           {selectedInvoice?.isArchived ? 'Permanently Delete Invoice' : 'Delete Invoice'}
         </DialogTitle>
         <DialogContent>
           <Typography variant="body1" sx={{ mb: 2 }}>
             Are you sure you want to {selectedInvoice?.isArchived ? 'permanently ' : ''}delete invoice{' '}
             <strong>{selectedInvoice?.invoiceNumber}</strong>?
           </Typography>
           {selectedInvoice?.isArchived ? (
             <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
               ⚠️ This action cannot be undone. The invoice will be permanently removed from the system.
             </Typography>
           ) : (
             <Typography variant="body2" color="text.secondary">
               This will permanently remove the invoice from the system.
             </Typography>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
           <Button onClick={handleDeleteConfirm} variant="contained" color="error">
             {selectedInvoice?.isArchived ? 'Permanently Delete' : 'Delete'}
           </Button>
         </DialogActions>
       </Dialog>

     </Box>
   </Container>
 );
};

export default InvoiceList;